import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import useNetworkStatus from '../../hooks/useNetworkStatus';
import useToast from '../../hooks/useToast';

// ─── Testimonials ───
const TESTIMONIALS = [
  { quote: "We closed 3 enterprise deals in the first week. The AI scoring alone paid for our annual plan.", author: "Sarah K.", role: "Sales Director, TechScale" },
  { quote: "iLeadX replaced our entire SDR team's manual prospecting. 10x ROI within 60 days.", author: "Marcus T.", role: "VP of Growth, RevenueOps" },
  { quote: "The enrichment engine finds emails we couldn't get anywhere else. Game-changer for outbound.", author: "Priya M.", role: "Head of Sales, CloudFirst" },
  { quote: "From search to pitch-ready lead in under 2 minutes. This is the future of B2B sales.", author: "James R.", role: "CEO, Pipeline AI" },
];

// ─── Live counter hook ───
function useLiveCounter(target = 128479, interval = 5000) {
  const [count, setCount] = useState(target - 147);
  useEffect(() => {
    const timer = setInterval(() => {
      setCount(prev => prev + Math.floor(Math.random() * 3) + 1);
    }, interval);
    return () => clearInterval(timer);
  }, [interval]);
  return count.toLocaleString();
}

// ─── Animated counter ───
function AnimatedCounter({ value }) {
  const [display, setDisplay] = useState('0');
  const prevRef = useRef('0');
  
  useEffect(() => {
    const prev = parseInt(prevRef.current.replace(/,/g, '')) || 0;
    const target = parseInt(value.replace(/,/g, '')) || 0;
    if (target === prev) { setDisplay(value); return; }
    
    const steps = 30;
    const increment = (target - prev) / steps;
    let current = prev;
    let step = 0;
    
    const timer = setInterval(() => {
      step++;
      current += increment;
      if (step >= steps) {
        setDisplay(target.toLocaleString());
        clearInterval(timer);
      } else {
        setDisplay(Math.round(current).toLocaleString());
      }
    }, 40);
    
    prevRef.current = target.toLocaleString();
    return () => clearInterval(timer);
  }, [value]);
  
  return <>{display}</>;
}

export default function Login() {
  const getLandingUrl = (path) => {
    const base = window.location.hostname === 'localhost' ? 'http://localhost:8000' : 'https://ileadx.io';
    return base + path;
  };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const inviteToken = new URLSearchParams(window.location.search).get('invite_token');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [mobilePanel, setMobilePanel] = useState('login'); // 'value' | 'login'
  const navigate = useNavigate();
  const { login, refreshUser } = useAuth();
  const { isOnline } = useNetworkStatus();
  const [searchParams] = useSearchParams();
  const lockReason = searchParams.get('reason') || '';
  const oauthError = searchParams.get('error') || '';
  const toastHook = useToast();
  const liveCount = useLiveCounter();

  // Rotate testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setTestimonialIndex(prev => (prev + 1) % TESTIMONIALS.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const handleLinkedInLogin = async () => {
    if (!termsAccepted) {
      toast('Terms Required', { description: 'Please accept the Terms of Service and Privacy Policy before signing in.', duration: 5000 });
      return;
    }
    try {
      const res = await api.get('/api/auth/linkedin', { params: { redirect: window.location.origin + '/dashboard' } });
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (err) {
      toast('LinkedIn sign-in failed.', { description: 'Please try again or use email to sign in.', duration: 5000 });
    }
  };

  const handleGoogleLogin = async () => {
    if (!termsAccepted) {
      toast('Terms & Privacy', { description: 'Please accept the Terms of Service and Privacy Policy to continue.', duration: 5000 });
      return;
    }
    try {
      const res = await api.get('/api/auth/google', { params: { redirect: window.location.origin + '/dashboard' } });
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (err) {
      toast('Google sign-in failed.', { description: 'Please try again or use email to sign in.', duration: 5000 });
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!isOnline) {
      setError('No internet connection. Please check your network and try again.');
      toast.error('Cannot login while offline', { id: 'login-offline' });
      return;
    }
    setLoading(true);
    setError('');
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'auto';
      const res = await api.post('/api/auth/login', { email, password, remember_me: rememberMe, timezone: tz });
      if (res.data.requires_2fa) {
        setTempToken(res.data.temp_token);
        setRequires2FA(true);
      } else {
        login(res.data.access_token, res.data.refresh_token, email);
        if (inviteToken) {
          try {
            await api.post(`/api/organization/invite/${inviteToken}/accept`);
            toast.success('Invitation accepted');
            await refreshUser();
          } catch {}
        }
        navigate('/dashboard');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid email or password';
      
      // Handle rate limiting
      if (err.response?.status === 429) {
        const retryAfter = err.response.data?.retry_after || 60;
        const mins = Math.ceil(retryAfter / 60);
        setError(`Too many login attempts. Please wait ${mins} minute${mins > 1 ? 's' : ''} before trying again.`);
        toast.error(`Rate limited,  wait ${mins} min`, { id: 'rate-limited', duration: 8000 });
        return;
      }
      
      setError(message);
      // Show lock banner if account is locked
      if (message.toLowerCase().includes('locked')) {
        window.history.replaceState({}, '', '/login?reason=locked');
        window.location.reload();
        return;
      }
      toast.error(message, { id: 'login-error' });
    } finally {
      setLoading(false);
    }
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/api/auth/verify-2fa', { temp_token: tempToken, code });
      localStorage.setItem('access_token', res.data.access_token);
      localStorage.setItem('refresh_token', res.data.refresh_token);
      login(res.data.access_token, res.data.refresh_token, email);
      toast.success('Welcome back!', { id: 'login-2fa-success', duration: 3000 });
      setTimeout(() => navigate('/dashboard'), 100);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code', { id: 'login-2fa-error' });
    } finally { setLoading(false); }
  };

  // ─── Shared input styles ───
  const labelStyle = {
    display: 'block',
    color: 'var(--text-secondary)',
    fontSize: 'var(--text-sm)',
    fontWeight: 500,
    marginBottom: 6,
    textAlign: 'left',
  };

  const inputFieldStyle = {
    width: '100%',
    padding: '12px 14px',
    background: 'var(--input-bg)',
    border: '1px solid var(--input-border)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--text-primary)',
    fontSize: 'var(--text-base)',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  };

  // ─── 2FA view ───
  if (requires2FA) {
    return (
      <>
        {/* Desktop: split screen */}
        <div className="auth-container" style={{ display: 'none' }} data-desktop>
          {/* Left: Value panel */}
          <div style={{
            width: '40%', minHeight: '100%',
            background: 'linear-gradient(160deg, #0A1628 0%, #0F1D3A 40%, #132244 100%)',
            display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
            padding: 'clamp(32px, 6vw, 48px)', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', inset: 0, opacity: 0.03,
              backgroundImage: `radial-gradient(circle at 30% 20%, #2563EB 1px, transparent 1px),
                                radial-gradient(circle at 70% 80%, #10B981 1px, transparent 1px)`,
              backgroundSize: '40px 40px',
            }} />
            <div style={{
              position: 'absolute',
              width: 'min(350px, 70vw)', height: 'min(350px, 70vw)',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(16, 185, 129, 0.06) 0%, transparent 70%)',
              top: '15%', right: '-15%',
            }} />
            <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 380, width: '100%' }}>
              <div style={{
                width: 80, height: 80, borderRadius: '50%',
                background: 'rgba(16, 185, 129, 0.1)',
                border: '2px solid rgba(16, 185, 129, 0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto var(--space-2xl)',
              }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                  <circle cx="12" cy="16" r="1" />
                </svg>
              </div>
              <div style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-xl)',
              }}>
                <p style={{ color: '#E2E8F0', fontSize: 'var(--text-base)', lineHeight: 1.7, fontStyle: 'italic', margin: 0 }}>
                  "Extra security that takes seconds. My entire team uses it without friction."
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', justifyContent: 'center', marginTop: 'var(--space-md)' }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #10B981, #059669)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: 'var(--text-xs)', fontWeight: 600,
                  }}>J</div>
                  <div>
                    <p style={{ color: '#F1F5F9', fontSize: 'var(--text-sm)', fontWeight: 600, margin: 0 }}>James K.</p>
                    <p style={{ color: '#94A3B8', fontSize: 'var(--text-xs)', margin: '2px 0 0' }}>CTO, RevenueStack</p>
                  </div>
                </div>
              </div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '8px 16px', marginTop: 'var(--space-xl)',
                background: 'rgba(16, 185, 129, 0.08)',
                border: '1px solid rgba(16, 185, 129, 0.15)',
                borderRadius: 'var(--radius-full)',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <span style={{ color: '#6EE7B7', fontSize: 'var(--text-xs)', fontWeight: 500 }}>
                  Protected by two-factor authentication
                </span>
              </div>
            </div>
          </div>

          {/* Right: 2FA form */}
          <div style={{
            width: '60%', minHeight: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 'clamp(24px, 6vw, 48px)',
            background: 'var(--bg-primary)',
          }}>
            <div style={{ width: '100%', maxWidth: 420 }}>
              {/* Brand */}
              <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }}>
                <div style={{ textAlign: 'center', marginBottom: 8 }}>
              <img src="/iLeadX-logo.png" alt="iLeadX" style={{ height: 40, width: 'auto' }} />
            </div>
            <h1 style={{
                  fontSize: 'var(--text-2xl)', fontWeight: 700,
                  color: 'var(--text-primary)', margin: '0 0 6px',
                  letterSpacing: '-0.5px',
                }}>
                  iLead<span style={{ color: 'var(--accent-primary)' }}>X</span>
                </h1>
                <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', margin: 0 }}>
                  Two-Factor Authentication
                </p>
              </div>

              <p style={{
                color: 'var(--text-secondary)', fontSize: 'var(--text-sm)',
                textAlign: 'center', margin: '0 0 24px', lineHeight: 1.5,
              }}>
                Enter the 6-digit code from your authenticator app to continue.
              </p>

              {error && <p className="error-message">{error}</p>}

              <form onSubmit={handle2FA}>
                {/* Digit boxes */}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 24 }}>
                  {[0, 1, 2, 3, 4, 5].map(i => (
                    <div key={i} style={{
                      width: 48, height: 56, borderRadius: 'var(--radius-md)',
                      border: code.length === i ? '2px solid var(--accent-primary)' : '1px solid var(--border-primary)',
                      background: 'var(--input-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 22, fontWeight: 700, fontFamily: 'monospace', color: 'var(--text-primary)',
                      boxShadow: code.length === i ? '0 0 0 3px var(--accent-glow)' : 'none',
                      transition: 'all 0.15s ease',
                    }}>
                      {code[i] || ''}
                    </div>
                  ))}
                </div>

                {/* Hidden input for mobile keyboard */}
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                  style={{
                    position: 'absolute', opacity: 0, width: 1, height: 1,
                  }}
                />

                <button type="submit" disabled={loading || code.length !== 6} className="auth-button auth-button-primary" style={{
                  height: 50, fontSize: 'var(--text-base)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  width: '100%', opacity: code.length === 6 ? 1 : 0.5,
                }}>
                  {loading ? (
                    <><span className="spinner" /> Verifying...</>
                  ) : (
                    <>Verify & Sign In</>
                  )}
                </button>
              </form>

              <p style={{ textAlign: 'center', marginTop: 20 }}>
                <button onClick={() => { setRequires2FA(false); setCode(''); }} style={{
                  background: 'none', border: 'none', color: 'var(--text-secondary)',
                  cursor: 'pointer', fontSize: 'var(--text-sm)',
                }}>
                  ← Back to Login
                </button>
              </p>
            </div>
          </div>
        </div>

        {/* Mobile: single column */}
        <div className="auth-container" style={{ flexDirection: 'column' }} data-mobile>
          <style>{`
            @media (min-width: 769px) { [data-mobile] { display: none !important; } [data-desktop] { display: flex !important; } }
            @media (max-width: 768px) { [data-mobile] { display: flex !important; } [data-desktop] { display: none !important; } }
          `}</style>
          <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(24px, 6vw, 48px)', background: 'var(--bg-primary)' }}>
            <div style={{ width: '100%', maxWidth: 420, animation: 'scaleIn 0.4s ease' }}>
              <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }}>
                <div style={{
                  width: 64, height: 64, borderRadius: '50%',
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '2px solid rgba(16, 185, 129, 0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto var(--space-lg)',
                }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0110 0v4" />
                    <circle cx="12" cy="16" r="1" />
                  </svg>
                </div>
                <div style={{ textAlign: 'center', marginBottom: 8 }}>
              <img src="/iLeadX-logo.png" alt="iLeadX" style={{ height: 40, width: 'auto' }} />
            </div>
            <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px' }}>Two-Factor Authentication</h1>
                <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', margin: 0 }}>Enter the code from your authenticator app</p>
              </div>

              {error && <p className="error-message">{error}</p>}

              <form onSubmit={handle2FA}>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 24 }}>
                  {[0, 1, 2, 3, 4, 5].map(i => (
                    <div key={i} style={{
                      width: 44, height: 52, borderRadius: 'var(--radius-md)',
                      border: code.length === i ? '2px solid var(--accent-primary)' : '1px solid var(--border-primary)',
                      background: 'var(--input-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 20, fontWeight: 700, fontFamily: 'monospace', color: 'var(--text-primary)',
                      boxShadow: code.length === i ? '0 0 0 3px var(--accent-glow)' : 'none',
                      transition: 'all 0.15s ease',
                    }}>
                      {code[i] || ''}
                    </div>
                  ))}
                </div>
                <input type="text" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} maxLength={6} autoFocus style={{ position: 'absolute', opacity: 0, width: 1, height: 1 }} />
                <button type="submit" disabled={loading || code.length !== 6} className="auth-button auth-button-primary" style={{ height: 50, fontSize: 'var(--text-base)', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: code.length === 6 ? 1 : 0.5 }}>
                  {loading ? <><span className="spinner" /> Verifying...</> : <>Verify & Sign In</>}
                </button>
              </form>

              <p style={{ textAlign: 'center', marginTop: 20 }}>
                <button onClick={() => { setRequires2FA(false); setCode(''); }} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--text-sm)' }}>
                  ← Back to Login
                </button>
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  const testimonial = TESTIMONIALS[testimonialIndex];

  // ─── Shared input styles ───
  
  const valuePanelContent = (
    <div style={{
      background: 'linear-gradient(160deg, #0A1628 0%, #0F1D3A 40%, #132244 100%)',
      minHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 'clamp(24px, 6vw, 48px)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Geometric mesh overlay */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.03,
        backgroundImage: `radial-gradient(circle at 20% 30%, #2563EB 1px, transparent 1px),
                          radial-gradient(circle at 80% 70%, #F59E0B 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
      }} />
      
      {/* Ambient orb */}
      <div style={{
        position: 'absolute',
        width: 'min(400px, 80vw)', height: 'min(400px, 80vw)',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(37, 99, 235, 0.08) 0%, transparent 70%)',
        top: '10%', right: '-10%',
        animation: 'glowPulse 4s ease-in-out infinite',
      }} />

      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 440, width: '100%' }}>
        {/* Shield + security indicator */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          background: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          borderRadius: 'var(--radius-full)',
          padding: '6px 16px',
          marginBottom: 'var(--space-2xl)',
        }}>
          <ShieldCheck size={16} color="#10B981" />
          <span style={{ color: '#6EE7B7', fontSize: 'var(--text-xs)', fontWeight: 500 }}>
            SOC 2 Compliant · Enterprise-Grade Security
          </span>
        </div>

        {/* Live counter */}
        <div style={{ marginBottom: 'var(--space-2xl)' }}>
          <div style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'clamp(1.75rem, 5vw, var(--text-5xl))',
            fontWeight: 700,
            color: '#60A5FA',
            lineHeight: 1,
            marginBottom: 'var(--space-sm)',
          }}>
            <AnimatedCounter value={liveCount} />
          </div>
          <p style={{
            color: '#94A3B8',
            fontSize: 'var(--text-sm)',
            margin: 0,
            letterSpacing: '0.5px',
          }}>
            leads generated today
          </p>
        </div>

        {/* Rotating testimonial */}
        <div style={{ minHeight: 120 }}>
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-xl)',
            textAlign: 'left',
          }}>
            <p style={{
              color: '#E2E8F0',
              fontSize: 'var(--text-base)',
              lineHeight: 1.7,
              margin: '0 0 var(--space-md)',
              fontStyle: 'italic',
            }}>
              "{testimonial.quote}"
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <div style={{
                width: 36, height: 36,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #2563EB, #3B82F6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontSize: 'var(--text-sm)', fontWeight: 600,
                flexShrink: 0,
              }}>
                {testimonial.author.charAt(0)}
              </div>
              <div>
                <p style={{ color: '#F1F5F9', fontSize: 'var(--text-sm)', fontWeight: 600, margin: 0 }}>
                  {testimonial.author}
                </p>
                <p style={{ color: '#94A3B8', fontSize: 'var(--text-xs)', margin: '2px 0 0' }}>
                  {testimonial.role}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Dots */}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 'var(--space-lg)' }}>
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              onClick={() => setTestimonialIndex(i)}
              style={{
                width: i === testimonialIndex ? 20 : 6,
                height: 6,
                borderRadius: 3,
                border: 'none',
                background: i === testimonialIndex ? '#60A5FA' : 'rgba(148, 163, 184, 0.3)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              aria-label={`Testimonial ${i + 1}`}
            />
          ))}
        </div>

        {/* Trust bar */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-md)',
          justifyContent: 'center',
          marginTop: 'var(--space-2xl)',
          color: '#94A3B8',
          fontSize: 'var(--text-xs)',
          opacity: 0.7,
        }}>
          <span>Trusted by 2,000+ teams</span>
          <span style={{ opacity: 0.3 }}>|</span>
          <span>99.9% Uptime</span>
        </div>
      </div>
    </div>
  );

  // ─── Login Form ───
  const loginForm = (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'clamp(24px, 6vw, 48px)',
      background: 'var(--bg-primary)',
      minHeight: '100%',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 420,
        animation: 'slideInRight 0.5s ease',
      }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }}>
          {(() => {
              const landingUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
                ? 'http://localhost:8000'
                : 'https://ileadx.io';
              return (
                <a href={landingUrl} style={{ display: 'block', textAlign: 'center', marginBottom: 8 }}>
                  <img src="/iLeadX-logo.png" alt="iLeadX" style={{ height: 48, width: 'auto' }} />
                </a>
              );
            })()}
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: 'var(--text-sm)',
            margin: 'var(--space-xs) 0 0',
          }}>
            <span style={{
              display: 'inline-block',
              background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 600,
              animation: 'aiSparkle 2s ease-in-out infinite',
            }}>AI</span>-Powered Lead Generation
          </p>
        </div>

        {/* OAuth error (Google cancel, etc.) */}
        {oauthError && (
          <div style={{
            padding: '12px 16px', background: 'var(--warning-bg)',
            borderRadius: 'var(--radius-md)', color: 'var(--warning)',
            fontSize: 13, marginBottom: 16, textAlign: 'center',
            border: '1px solid rgba(245,158,11,0.2)',
          }}>
            {oauthError}
          </div>
        )}

        {/* Locked account */}
        {(lockReason && lockReason.includes('lock')) && (
          <div style={{
            padding: '12px 16px', background: 'var(--error-bg)',
            borderRadius: 'var(--radius-md)', color: 'var(--error)',
            fontSize: 13, marginBottom: 16, textAlign: 'center',
          }}>
            🔒 Your account has been locked. Contact support for assistance.
          </div>
        )}

        {/* Error */}
        {error && <p className="error-message">{error}</p>}

        {/* Form */}
        <form onSubmit={handleLogin}>
          {/* Email */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle} htmlFor="login-email">Email address</label>
            <div style={{ position: 'relative' }}>
              <Mail size={18} style={{
                position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--text-tertiary)', pointerEvents: 'none',
              }} />
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                style={{ ...inputFieldStyle, paddingLeft: 42 }}
                required
                autoComplete="email"
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--accent-primary)';
                  e.target.style.boxShadow = '0 0 0 3px var(--accent-glow)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--input-border)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>

          {/* Password */}
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle} htmlFor="login-password">Password</label>
            <div style={{ position: 'relative' }}>
              <Lock size={18} style={{
                position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--text-tertiary)', pointerEvents: 'none',
              }} />
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                style={{ ...inputFieldStyle, paddingLeft: 42, paddingRight: 42 }}
                required
                autoComplete="current-password"
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--accent-primary)';
                  e.target.style.boxShadow = '0 0 0 3px var(--accent-glow)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--input-border)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', color: 'var(--text-tertiary)',
                  cursor: 'pointer', padding: 4, display: 'flex',
                }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Remember me + Forgot password */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 24,
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              color: 'var(--text-secondary)',
              fontSize: 'var(--text-sm)',
              cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ accentColor: 'var(--accent-primary)', width: 16, height: 16 }}
              />
              Stay logged in
            </label>
            <Link to="/forgot-password" style={{
              color: 'var(--text-link)',
              fontSize: 'var(--text-sm)',
              textDecoration: 'none',
              fontWeight: 500,
            }}>
              Forgot password?
            </Link>
          </div>

          {/* CTA */}
          <button
            type="submit"
            disabled={loading}
            className="auth-button auth-button-primary"
            style={{
              height: 50,
              fontSize: 'var(--text-base)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-sm)',
            }}
          >
            {loading ? (
              <>
                <span className="spinner" /> Signing in...
              </>
            ) : 'Sign In'}
          </button>
        </form>

        {/* Divider */}
        <div className="auth-divider" style={{ margin: '28px 0' }}>or</div>

        {/* OAuth */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
            <input type="checkbox" id="terms-oauth" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} style={{ accentColor: 'var(--accent-primary)', width: 16, height: 16, cursor: 'pointer' }} />
            <label htmlFor="terms-oauth" style={{ color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer' }}>
              I agree to the <a href={getLandingUrl("/terms")} target="_blank" style={{ color: 'var(--accent-primary)', textDecoration: 'none' }}>Terms</a> and <a href={getLandingUrl("/privacy")} target="_blank" style={{ color: 'var(--accent-primary)', textDecoration: 'none' }}>Privacy Policy</a>
            </label>
          </div>
          <button
          onClick={handleGoogleLogin}
          style={{
            width: '100%', height: 48, borderRadius: 10,
            border: '1px solid var(--border-primary)', background: 'var(--bg-secondary)',
            color: 'var(--text-primary)', cursor: 'pointer', fontSize: 14, fontWeight: 500,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            transition: 'all 0.15s ease', fontFamily: 'inherit',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-tertiary)'; e.currentTarget.style.borderColor = 'var(--accent-primary)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-secondary)'; e.currentTarget.style.borderColor = 'var(--border-primary)'; }}
        >
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign in with Google
        </button>

        {/* LinkedIn Sign In */}
        <button
          onClick={handleLinkedInLogin}
          style={{
            width: '100%', height: 48, borderRadius: 10,
            border: '1px solid var(--border-primary)', background: 'var(--bg-secondary)',
            color: 'var(--text-primary)', cursor: 'pointer',
            fontSize: 14, fontWeight: 500, marginTop: 12,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            transition: 'all 0.15s ease', fontFamily: 'inherit',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-tertiary)'; e.currentTarget.style.borderColor = '#0A66C2'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-secondary)'; e.currentTarget.style.borderColor = 'var(--border-primary)'; }}
        >
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path fill="#0A66C2" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
          Sign in with LinkedIn
        </button>

        {/* Sign up link */}
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: 'var(--text-sm)',
          marginTop: 28,
          textAlign: 'center',
        }}>
          Don't have an account?{' '}
          <Link to="/register" style={{
            color: 'var(--accent-primary)',
            fontWeight: 600,
            textDecoration: 'none',
          }}>
            Start free trial
          </Link>
        </p>
      </div>
    </div>
  );

  // ─── Render ───
  return (
    <>
      {/* Desktop: split screen */}
      <div className="auth-container" style={{ display: 'none' }} data-desktop>
        <div style={{ width: '45%', minHeight: '100%' }}>{valuePanelContent}</div>
        <div style={{ width: '55%', minHeight: '100%' }}>{loginForm}</div>
      </div>

      {/* Mobile: stacked with toggle */}
      <div className="auth-container" style={{ flexDirection: 'column' }} data-mobile>
        <style>{`
          @media (min-width: 769px) {
            [data-mobile] { display: none !important; }
            [data-desktop] { display: flex !important; }
          }
          @media (max-width: 768px) {
            [data-mobile] { display: flex !important; }
            [data-desktop] { display: none !important; }
          }
        `}</style>

        <div style={{ flex: 1, overflow: 'auto' }}>
            {loginForm}
          </div>
      </div>
    </>
  );
}
